import React, { PureComponent } from "react"
import { Col, Container, Modal, Row } from "react-bootstrap"
import { toast } from "react-toastify"
import { Subscription } from "rxjs"
import GovSendToLab, { GovMarkAsComplete, SendResult } from "../../api/Randoms/GovPerformCollection"
import SelectedGovPanel, { GovPerformCollectionData } from "../../api/Randoms/GovPoolModel/GovPerformCollection"
import ActionButton from "../ActionButton"
import printPdf from "../../utils/printPdf"
import DonorInfo from "../GovPerformCollection/DonorInfo"
import RecordTemperature, { Temperature } from "../GovPerformCollection/RecordTemperature"
import { finalize } from "rxjs/operators"
import InputSpecimenId from "../GovPerformCollection/InputSpecimenId"
import CollectorNotes from "../GovPerformCollection/CollectorNotes"
import FormatError from "../../formatters/FormatError"
import Panels from "../GovPerformCollection/Panels"
import { PanelResult } from "../../api/GovPerformCollection/PanelResult"

export interface GovPerformCollectionProps {
  testName?: string
  testDetailID: number
  testDetailIDHash: string
  referenceID: number
  sampleTypeCode: string
  sampleTypeID: number
  labCode: string
  sampleID: string
  tempInRange?: number
  personID?: number
  firstName?: string
  lastName?: string
  dateOfBirth?: Date
  gender?: string
  collectorNotes?: string
  onHide: (collected?: boolean) => void
  DeviceID: number
  AccountID: number
}

interface GovPerformCollectionState {
  loading: boolean
  collected: boolean
  sendingToLab: boolean
  markingAsComplete: boolean
  isTempRangeValid: boolean
  isSampleIdValid: boolean
  validate: boolean | undefined
  data: GovPerformCollectionData
}

export default class GovPerformCollection extends PureComponent<GovPerformCollectionProps, GovPerformCollectionState> {
  state: GovPerformCollectionState = {
    loading: false,
    collected: false,
    sendingToLab: false,
    markingAsComplete: false,
    isTempRangeValid: this.props.tempInRange !== Temperature.NOT_SELECTED,
    isSampleIdValid: true,
    validate: undefined,
    data: {
      TestDetailID: this.props.testDetailID,
      TestDetailIDHash: this.props.testDetailIDHash,
      ReferenceID: this.props.referenceID,
      SampleTypeCode: this.props.sampleTypeCode || "",
      SampleTypeID: this.props.sampleTypeID,
      TempInRange: this.props.tempInRange || Temperature.NOT_SELECTED,
      SampleID: ["N/P"].includes(this.props.sampleID) ? "" : this.props.sampleID,
      CollectorNotes: this.props.collectorNotes || "",
      SelectedPanels: [],
      DeviceID: this.props.DeviceID || 0
    }
  }

  markAsCompleteSubscription?: Subscription = undefined
  sendToLabSubscription?: Subscription = undefined

  componentWillUnmount() {
    this.markAsCompleteSubscription && this.markAsCompleteSubscription.unsubscribe()
    this.sendToLabSubscription && this.sendToLabSubscription.unsubscribe()
  }

  withoutNotAvailable = () => this.state.data.SelectedPanels.filter(p => p.ExternalID !== "")

  isAllMainPanelsSelected = (type: number) => {
    const panels = this.withoutNotAvailable().filter(p => !this.isAdditional(p))
    return panels.length > 0 && panels.every(p => p.SelectedStatus === type)
  }

  isUrineSample = () => ["UR", "QT"].includes(this.props.sampleTypeCode)

  isPOCTSample = () => ["QT", "QTS"].includes(this.props.sampleTypeCode)

  isLabSample = () => ["UR", "SLD"].includes(this.props.sampleTypeCode)

  hasSendToLabPanels = () => this.withoutNotAvailable().some(p => p.SendToLab === 1)

  isCanMarkAsComplete = () => !this.hasSendToLabPanels()

  isCanSendToLab = () => this.isLabSample() || this.hasSendToLabPanels()

  isAdditional = (panel: SelectedGovPanel) => panel.Code.startsWith("W")

  showMarkAsComplete = () => {
    return this.isPOCTSample() &&
      !this.withoutNotAvailable().some(p => p.SelectedStatus === PanelResult.NOT_SELECTED && !this.isAdditional(p));
  }

  showCanSendToLab = () => {
    return this.isLabSample() || this.withoutNotAvailable()
      .some(p => p.SendToLab === 1);
  }

  resultProcessing = (data: SendResult) => {
    if (data.Success) {
      if (data.ImageData !== "") {
        printPdf(data.ImageData)
      }
      this.setState({ collected: true }, this.onHide);
    } else {
      toast.error(data.Text)
    }
  }

  onMarkAsComplete = () => {
    this.setState({ markingAsComplete: true })
    if (!this.validateForm()) {
      this.setState({ markingAsComplete: false })
      return
    }

    const panels = this.state.data.SelectedPanels.filter(p=> !this.isAdditional(p))
    const data = {...this.state.data, SelectedPanels: panels}

    this.markAsCompleteSubscription && this.markAsCompleteSubscription.unsubscribe()
    this.markAsCompleteSubscription = GovMarkAsComplete(data)
      .pipe(finalize(() => this.setState({ markingAsComplete: false })))
      .subscribe(
        data => this.resultProcessing(data),
        error => toast.error(FormatError(error))
      )
  }

  onSendToLab = () => {
    this.setState({ sendingToLab: true })
    if (!this.validateForm()) {
      this.setState({ sendingToLab: false })
      return
    }

    this.sendToLabSubscription && this.sendToLabSubscription.unsubscribe()
    this.sendToLabSubscription = GovSendToLab(this.state.data)
      .pipe(finalize(() => this.setState({ sendingToLab: false })))
      .subscribe(
        data => this.resultProcessing(data),
        error => toast.error(FormatError(error))
      )
  }

  onHide = () => this.props.onHide(this.state.collected)

  onChangeTemperature = (value: number, isValid: boolean) =>
    this.setState(prev => ({ ...prev, isTempRangeValid: isValid, data: { ...prev.data, TempInRange: value } }))

  onChangeSpecimenId = (value: string, isValid: boolean) =>
    this.setState(prev => ({ ...prev, isSampleIdValid: isValid, validate: true, data: { ...prev.data, SampleID: value } }))

  onChangeCollectorNotes = (value: string) =>
    this.setState(prev => ({ ...prev, data: { ...prev.data, CollectorNotes: value } }))

  onUpdatePanels = (panels: SelectedGovPanel[]) =>
    this.setState(prev => ({ ...prev, data: { ...prev.data, SelectedPanels: panels } }))

  onSetLoading = (loading: boolean) =>
    this.setState({ loading: loading })

  validateTemp = () => !this.isUrineSample() || this.state.isTempRangeValid

  validateForm = () => {
    this.setState(prev => ({ ...prev, validate: !prev.validate }))
    return this.state.isSampleIdValid && this.validateTemp()
  }

  render() {
    const { loading, markingAsComplete, sendingToLab } = this.state
    const { SampleID, TempInRange } = this.state.data

    return (
      <Modal backdrop="static" show={true} onHide={this.onHide} size="xl" dialogClassName="dark">
        <Modal.Header closeButton={true}>
          <Modal.Title>PERFORM COLLECTION</Modal.Title>
        </Modal.Header>
        <Modal.Body className="event-details">
          <Container fluid className="grid-container event-grid">
            <DonorInfo
              personID={this.props.personID}
              firstName={this.props.firstName}
              lastName={this.props.lastName}
              dateOfBirth={this.props.dateOfBirth}
              gender={this.props.gender}
              />
            {this.props.testName && <Container className="pt-3"><Row><Col md={3}><h6>Test to be performed:</h6></Col><Col className="mx-2">{this.props.testName}</Col></Row></Container>}
            {(this.isUrineSample()) && <RecordTemperature
              temperature={TempInRange}
              required={this.isUrineSample()}
              disabled={loading}
              validate={this.state.validate}
              onChange={this.onChangeTemperature} />}
            <Panels
              labCode={this.props.labCode}
              sampleTypeCode={this.props.sampleTypeCode}
              onUpdate={this.onUpdatePanels}
              onLoading={this.onSetLoading}
              deviceID={this.props.DeviceID}
              accountID={this.props.AccountID} />
            <InputSpecimenId
              sampleId={SampleID}
              disabled={loading}
              required={this.isCanSendToLab()}
              validate={this.state.validate}
              onChange={this.onChangeSpecimenId} />
            <CollectorNotes
              disabled={loading}
              notes={this.state.data.CollectorNotes}
              onChange={this.onChangeCollectorNotes} />
          </Container>
        </Modal.Body>
        {<Modal.Footer>
          {this.showCanSendToLab() && <ActionButton variant="primary"
            disabled={!this.isCanSendToLab() || loading || sendingToLab}
            onClick={this.onSendToLab}
            spin={sendingToLab}>Send To Lab</ActionButton>}
          {this.showMarkAsComplete() && <ActionButton variant="primary"
            disabled={!this.isCanMarkAsComplete() || loading || markingAsComplete}
            onClick={this.onMarkAsComplete}
            spin={markingAsComplete}>Mark as Complete</ActionButton>}
        </Modal.Footer>}
      </Modal>
    )
  }
}
